import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SearchRow from 'views/AddNewServices/AddSingleService/tabs/common/SearchRow';
import { ProductsService } from 'services/products/products';
import styles from 'assets/jss/material-dashboard-pro-react/views/services/addLineDialogStyle';
import { makeStyles } from '@material-ui/core';
import { SingleBillId } from 'utils/api/models/bills/singleBillId';
import classNames from 'classnames';
import { useLocation } from 'react-router';
import { AccountType } from 'utils/api/models/accounts/accountType';
import { BasicPlan, Commitments, ProductsDetailsResponse } from 'utils/api/models/postpaidLines/postpaidLines';
import { PostpaidLinesTable } from 'components/ServicesTables/PostpaidLinesTable';
import SimpleTabs from 'components/SimpleTabs/SimpleTabs';
import { BillingType } from 'utils/api/models/accounts/singleAccount';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import { finalize } from 'rxjs/operators';
import { hiddenPlans } from 'components/ServicesTables/PostpaidLinesTable/constants';

// @ts-ignore
const useStyles = makeStyles(styles);

const useQuery = () => {
	return new URLSearchParams(useLocation().search);
};

interface IPlanTabProps {
	billingAccounts: SingleBillId[];
	setTariffPlan: (e: any) => void;
	selectedBillingAccount: types.newServices.IBillingId | null;
	setSelectedBillingAccount: React.Dispatch<React.SetStateAction<types.newServices.IBillingId | null>>;
	baseNumber: types.newServices.IBaseNumber | null;
	setBaseNumber: React.Dispatch<React.SetStateAction<types.newServices.IBaseNumber | null>>;
	setNamaBundleCommitmentId: React.Dispatch<React.SetStateAction<string>>;
	selectedTariff?: BasicPlan | null;
	setSelectedTariff: React.Dispatch<React.SetStateAction<BasicPlan | null>>;
	setSelectedCommitment: React.Dispatch<React.SetStateAction<Commitments | null>>;
	setProductsDetails: React.Dispatch<React.SetStateAction<ProductsDetailsResponse | null>>;
	setCommitmentDuration: React.Dispatch<React.SetStateAction<string | null>>;
	commitmentDuration?: string | null;
	activeTab: number;
	setActiveTab: React.Dispatch<React.SetStateAction<number>>;
}

const PlanTab = (props: IPlanTabProps) => {
	const classes = useStyles();
	const {
		billingAccounts,
		setTariffPlan,
		selectedBillingAccount,
		setSelectedBillingAccount,
		setBaseNumber,
		baseNumber,
		setNamaBundleCommitmentId,
		selectedTariff,
		setSelectedTariff,
		setSelectedCommitment,
		setProductsDetails,
		setCommitmentDuration,
		commitmentDuration,
		activeTab,
		setActiveTab,
	} = props;
	const isMounted = useRef(true);
	const query = useQuery();
	const selectedBillingId = query.get('billingId');

	const [productFilters, setProductFilters] = useState<string[] | null>(null);
	const [loading, setLoading] = useState(false);
	const [commitment, setCommitment] = useState<number | null>(commitmentDuration ? +commitmentDuration : null);

	const tabs = useMemo(() => {
		return productFilters?.map((pf) => {
			return {
				name: pf,
				content: (
					<PostpaidLinesTable
						accountId={selectedBillingAccount!.billingId}
						setSelectedTariff={setSelectedTariff}
						setSelectedCommitment={setSelectedCommitment}
						setProductsDetails={setProductsDetails}
						selectedProductFilter={pf}
						setCommitmentDuration={setCommitmentDuration}
						commitmentDuration={commitmentDuration}
						selectedTariff={selectedTariff}
						commitment={commitment}
						setCommitment={setCommitment}
					/>
				),
			};
		});
	}, [
		productFilters,
		commitmentDuration,
		selectedBillingAccount,
		selectedTariff,
		commitment,
		setCommitmentDuration,
		setProductsDetails,
		setSelectedCommitment,
		setSelectedTariff,
		setCommitment,
	]);

	const extendedBillingAccounts = useMemo(() => {
		return billingAccounts.map((billingAccount, index) => ({
			id: `${billingAccount.billingId}${index}`,
			accountId: billingAccount.accountId,
			billingId: billingAccount.billingId,
			value: `${billingAccount.billingAccountName} / ${billingAccount.billingId}`,
			content: <SearchRow text={`${billingAccount.billingAccountName} / ${billingAccount.billingId}`} />,
		}));
	}, [billingAccounts]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const selectBillingAccount = useCallback(
		(billingAccount: types.newServices.IBillingId | null) => {
			setBaseNumber(null);
			setTariffPlan(null);
			setSelectedBillingAccount(billingAccount);
		},
		[setBaseNumber, setSelectedBillingAccount, setTariffPlan]
	);

	useEffect(() => {
		if (extendedBillingAccounts && selectedBillingId) {
			// check if billing account id is set in query params
			if (selectedBillingId) {
				const billingAccount = extendedBillingAccounts.find(
					(singleBillingAccount) => singleBillingAccount.billingId === selectedBillingId
				);
				// mark billing account as set in CustomSearch component
				if (billingAccount && selectBillingAccount) {
					selectBillingAccount(billingAccount);
				}
			}
		}
	}, [extendedBillingAccounts, selectedBillingId, selectBillingAccount]);

	useEffect(() => {
		if (baseNumber) {
			ProductsService.getCustomerProducts({
				phoneNumber: baseNumber.accountNum,
				accountType: baseNumber.accountType,
				billingType: baseNumber.billingType,
			}).subscribe(({ tariffPlan, namaCommitment }) => {
				if (isMounted.current) {
					setTariffPlan(tariffPlan(baseNumber.accountType as AccountType));
					setNamaBundleCommitmentId(namaCommitment?.productId || '');
				}
			});
		}
	}, [baseNumber, setTariffPlan, setNamaBundleCommitmentId]);

	useEffect(() => {
		if (selectedBillingAccount?.billingId) {
			setLoading(true);
			const subscription = ProductsService.getProductsDetails(
				selectedBillingAccount.billingId,
				BillingType.POSTPAID
			)
				.pipe(finalize(() => setLoading(false)))
				.subscribe(({ productDetails }) => {
					setProductFilters(productDetails.tariffPlans.map((tp) => tp.name));
				});
			return () => {
				subscription.unsubscribe();
			};
		}
	}, [selectedBillingAccount, setProductsDetails]);

	const handleSetActiveTab = (e: React.SetStateAction<number>) => {
		setSelectedCommitment(null);
		setCommitmentDuration(null);
		setCommitment(null);
		setActiveTab(e);
	};

	return (
		<div className={classNames(classes.planTabWrapper)}>
			<ConditionalRender
				show={loading}
				onTrue={() => <CustomCircularProgress loading text="" transparent />}
				onFalse={() => (
					<>
						{selectedBillingAccount && tabs && (
							<div>
								<SimpleTabs tabs={tabs} activeTab={activeTab} setActiveTab={handleSetActiveTab} />
							</div>
						)}
					</>
				)}
			/>
		</div>
	);
};

export default PlanTab;
